import React, { useMemo, useState } from "react";
import { Button, Grid, MenuItem, Stack, TextField, Typography } from "@mui/material";

import { PagedTable, SectionCard, StatusChip, formatCurrency, formatDateTime, safeArray, toTitleCase } from "../coreNetworkUiShared";

const emptyForm = {
  planCode: "featured",
  status: "active",
  startsAt: "",
  endsAt: "",
  billingCycle: "monthly",
  amount: "",
  currency: "CAD",
  sponsoredPlacement: "true",
};

export default function CoreNetworkSubscriptionPanel({
  subscriptions = [],
  membershipId = null,
  saving = false,
  canManage = false,
  onCreateSubscription,
  onUpdateSubscription,
}) {
  const [form, setForm] = useState(emptyForm);

  const orderedSubscriptions = useMemo(
    () => [...safeArray(subscriptions)].sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)),
    [subscriptions]
  );

  return (
    <SectionCard title="Subscriptions & Placement" subtitle="Featured and sponsored placement controls are tracked here and must remain explicitly disclosed in matching.">
      <Stack spacing={3}>
        <PagedTable
          rows={orderedSubscriptions}
          columns={[
            { key: "planCode", label: "Plan", render: (row) => toTitleCase(row.planCode) },
            { key: "status", label: "Status", render: (row) => <StatusChip value={row.status} /> },
            { key: "scope", label: "Scope", render: (row) => row.instance?.instanceName || "Global" },
            { key: "amount", label: "Amount", render: (row) => formatCurrency(row.amount, row.currency || "CAD") },
            { key: "startsAt", label: "Starts", render: (row) => formatDateTime(row.startsAt) },
            { key: "endsAt", label: "Ends", render: (row) => formatDateTime(row.endsAt) },
            {
              key: "actions",
              label: "",
              render: (row) =>
                canManage ? (
                  <Button size="small" onClick={() => onUpdateSubscription?.(row.id, { status: row.status === "active" ? "inactive" : "active" })}>
                    {row.status === "active" ? "Disable" : "Enable"}
                  </Button>
                ) : null,
            },
          ]}
          emptyTitle="No subscriptions yet"
          emptyDescription="Featured or premium placement controls will appear here."
        />

        {canManage && onCreateSubscription ? (
          <Stack spacing={2}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Add subscription
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <TextField fullWidth label="Plan code" value={form.planCode} onChange={(event) => setForm((prev) => ({ ...prev, planCode: event.target.value }))} />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField select fullWidth label="Status" value={form.status} onChange={(event) => setForm((prev) => ({ ...prev, status: event.target.value }))}>
                  {["active", "inactive", "cancelled", "expired"].map((option) => (
                    <MenuItem key={option} value={option}>
                      {toTitleCase(option)}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField fullWidth label="Billing cycle" value={form.billingCycle} onChange={(event) => setForm((prev) => ({ ...prev, billingCycle: event.target.value }))} />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField fullWidth label="Amount" value={form.amount} onChange={(event) => setForm((prev) => ({ ...prev, amount: event.target.value }))} />
              </Grid>
              <Grid item xs={12} md={1.5}>
                <TextField fullWidth label="Currency" value={form.currency} onChange={(event) => setForm((prev) => ({ ...prev, currency: event.target.value }))} />
              </Grid>
              <Grid item xs={12} md={1.5}>
                <TextField select fullWidth label="Sponsored" value={form.sponsoredPlacement} onChange={(event) => setForm((prev) => ({ ...prev, sponsoredPlacement: event.target.value }))}>
                  <MenuItem value="true">Yes</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth type="datetime-local" InputLabelProps={{ shrink: true }} label="Starts At" value={form.startsAt} onChange={(event) => setForm((prev) => ({ ...prev, startsAt: event.target.value }))} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth type="datetime-local" InputLabelProps={{ shrink: true }} label="Ends At" value={form.endsAt} onChange={(event) => setForm((prev) => ({ ...prev, endsAt: event.target.value }))} />
              </Grid>
            </Grid>
            <div>
              <Button
                variant="outlined"
                disabled={saving || !form.planCode}
                onClick={async () => {
                  await onCreateSubscription({
                    membershipId,
                    planCode: form.planCode,
                    status: form.status,
                    startsAt: form.startsAt || null,
                    endsAt: form.endsAt || null,
                    billingCycle: form.billingCycle,
                    amount: form.amount || null,
                    currency: form.currency,
                    benefitsJson: {
                      sponsoredPlacement: form.sponsoredPlacement === "true",
                    },
                  });
                  setForm(emptyForm);
                }}
              >
                {saving ? "Saving..." : "Add subscription"}
              </Button>
            </div>
          </Stack>
        ) : null}
      </Stack>
    </SectionCard>
  );
}
